import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Invoice } from '../../model/invoice';
import { canonicalizeBusinessIdControl, clientBusinessIdValidator } from '../../validators/business-id/angular-adapter';
import { CountriesMap, CountriesMapCache } from '../../model/model-map-cache';
import { Client, ClientRepresentative, Invoice as ModernInvoice, Permission } from 'src/app/api/models';
import { ClientsService } from 'src/app/api/services';
import { UserService } from 'src/app/service/user.service';
import { AAARatings } from 'src/app/enum/aaa-ratings';

// This widget is for CompanyClient. A new widget will be needed for LightEntrepreneurs.
interface ClientAndRepresentative {
  client: Client,
  representative: ClientRepresentative,
  invoice: ModernInvoice,
}

@Component({
  selector: 'app-invoice-client-widget',
  templateUrl: './invoice-client-widget.component.html',
  styleUrls: ['./invoice-client-widget.component.scss']
})
export class InvoiceClientWidgetComponent implements OnInit {

  @Input()
  expanded: boolean;

  countries$: Observable<CountriesMap>;
  aaaRatings = AAARatings;

  invoice$: Observable<Invoice>;
  clientAndRepresentative$: Observable<ClientAndRepresentative>;

  form: UntypedFormGroup;

  /**
   * Whether the user may save the client, which the edit form's Save and the
   * "Hold to update timestamp" button do through PUT /clients/{id}
   * (clients:edit, issue #678: a rename starts a Verification of Payee
   * check). Unknown, not missing, until the current user loads.
   */
  readonly canEditClient$: Observable<boolean>;
  readonly editPermissionTooltip = `Requires the ${Permission.ClientsEdit} permission`;

  edited = false;
  locked = false;

  constructor(
    private builder: UntypedFormBuilder,
    private invoicePeerService: InvoicePeerService,
    private clientsService: ClientsService,
    private countriesMapCache: CountriesMapCache,
    userService: UserService
  ) {
    this.canEditClient$ = userService.hasPermission(Permission.ClientsEdit);
    this.form = this.builder.group({
      client: this.builder.group({
        name: [null, Validators.required],
        country: ['FI', Validators.required],
        business_id: [null, [Validators.required, clientBusinessIdValidator()]],
        company_form_code: [null],
        company_form_other: [null],
        is_in_prepayment_register: [{ value: null, disabled: true }],
        mark_prepayment_register_checked: [false],
        aaa_rating: this.builder.group({
          category: [null],
        }),
      }),
      representative: this.builder.group({
        first_name: [null, Validators.required],
        last_name: [null, Validators.required],
        email: [null, Validators.email],
        phone: [null]
      })
    });

    this.form.get('client.country').valueChanges
      .subscribe((country) => {
        const businessId = this.form.get('client.business_id');
        if (businessId) {
          businessId.markAsTouched();
          businessId.updateValueAndValidity();
        }

        const companyFormCode = this.form.get('client.company_form_code');
        const companyFormOther = this.form.get('client.company_form_other');

        if (country === 'FI') {
          companyFormCode.setValidators(Validators.required);
          companyFormOther.clearValidators();
          companyFormOther.setValue(null);
        } else {
          companyFormCode.clearValidators();
          companyFormCode.setValue(null);
          companyFormOther.setValidators(Validators.required);
        }
        companyFormCode.updateValueAndValidity();
        companyFormOther.updateValueAndValidity();
      });

    this.form.get('client.mark_prepayment_register_checked').valueChanges.subscribe(checked => {
      const control = this.form.get('client.is_in_prepayment_register');
      if (checked) {
        control.enable();
      } else {
        control.disable();
      }
    });
  }

  edit(client: Client, representative: ClientRepresentative): void {
    this.invoicePeerService.lock();
    this.edited = true;
    this.form.reset();
    this.form.patchValue({
      client: {
        ...client,
        is_in_prepayment_register: client.prepayment_register?.is_in_register,
      },
      representative: representative,
    });
    this.form.get('client.mark_prepayment_register_checked').setValue(false);
    // business_id is not operator-editable on an existing client: disabling it
    // keeps a legacy non-conforming id from invalidating the form (blocking
    // Save) and lets the canonicalize helper skip it (design D10).
    this.form.get('client.business_id').disable();
  }

  revert(): void {
    this.edited = false;
    this.invoicePeerService.unlock();
    this.form.reset();
  }

  save(client: Client, representative: ClientRepresentative): void {
    // business_id is disabled in edit mode (a stored value), so the helper skips
    // it and getRawValue() below submits it byte-identical (design D10).
    canonicalizeBusinessIdControl(this.form.get('client.business_id'));

    // AI: getRawValue() is needed to get values from disabled form controls.
    const formClientValue = this.form.get('client').getRawValue();
    const { aaa_rating, is_in_prepayment_register, mark_prepayment_register_checked, ...restOfClientData } = formClientValue;
    Object.assign(client, restOfClientData);
    Object.assign(representative, this.form.value.representative);

    // AI: Handle prepayment register update.
    if (mark_prepayment_register_checked) {
      client.prepayment_register = {
        is_in_register: is_in_prepayment_register,
        mark_checked: true,
      };
    }

    // AI: Handle aaa_rating update. The form only contains the category.
    if (aaa_rating && aaa_rating.category) {
      if (!client.aaa_rating || client.aaa_rating.category !== aaa_rating.category) {
        // If it's a new rating or it has changed, mark it for update.
        // The backend will set checked_at.
        client.aaa_rating = {
          category: aaa_rating.category,
          mark_checked: true,
          // AI: Add a real timestamp for checked_at to satisfy the type-checker.
          // The backend will ignore this read-only field, but validates its format.
          checked_at: new Date().toISOString(),
        };
      }
      // If category is unchanged, do nothing to preserve checked_at.
    } else {
      // The rating is cleared in the form, so we set it to null.
      client.aaa_rating = null;
    }

    this.clientsService.updateClient({ id: client.id, body: client }).subscribe();
    this.clientsService.updateClientRepresentative({
      client_id: representative.client_id,
      id: representative.id,
      body: representative
    }).subscribe(() => {
      this.invoicePeerService.unlock();
      this.invoicePeerService.refresh();
      this.edited = false;
    });
  }

  dateToString(date: Date): string {
    return date.toISOString();
  }

  touchCreditLimitAndRating(client: Client) {
    if (!client.id || this.edited) {
      return;
    }

    if (client.credit_limit) {
      client.credit_limit.mark_checked = true;
    }

    if (client.aaa_rating) {
      client.aaa_rating.mark_checked = true;
    }

    this.clientsService.updateClient({ id: client.id, body: client }).subscribe(() => {
      this.invoicePeerService.refresh();
    });
  }

  ngOnInit() {
    this.countries$ = this.countriesMapCache.map$;

    this.clientAndRepresentative$ = this.invoicePeerService
      .getInvoice()
      .pipe(
        switchMap((invoice: ModernInvoice) => {
          if (!invoice || !invoice.client) {
            return EMPTY;
          }
          return this.clientsService.getClientRepresentative({ client_id: invoice.client_id, id: invoice.client_representative_id }).pipe(
            map(representative => ({ client: invoice.client, representative: representative, invoice: invoice }))
          );
        }),
        catchError(err => {
          console.error('AI: Error loading client, representative and invoice data in Company client widget:', err);
          return EMPTY;
        })
      );

    this.invoicePeerService.getState().subscribe((state: string) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });
  }
}
